// Inline-SVG mini previews of elements for panel rows — replaces v1's SVG.js
// thumbnails with dependency-free string building. Pure: element data in,
// `<svg>` markup out.

import { coordsToPathData } from '../canvas/geometry';
import { esc } from './html';

const SIZE = 22; // rendered box (px); viewBox is the element's own bounds

function styleAttrs(d) {
  const fill = d.fillColor || 'none';
  const stroke = d.strokeColor || (d.fillColor ? 'none' : '#555');
  return `fill="${esc(fill)}" stroke="${esc(stroke)}" vector-effect="non-scaling-stroke" stroke-width="1"`;
}

// Bounds of the drawable geometry, centred coordinates for rect/ellipse.
function box(el) {
  const d = el.data || {};
  if (el.type === 'ellipse' || el.type === 'rectangle') {
    const hw = (d.width || 0) / 2; const hh = (d.height || d.width || 0) / 2;
    return { x: d.x - hw, y: d.y - hh, w: hw * 2, h: hh * 2 };
  }
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const c of d.coordinates || []) {
    if (Array.isArray(c) && typeof c[0] === 'number') {
      minX = Math.min(minX, c[0]); maxX = Math.max(maxX, c[0]);
      minY = Math.min(minY, c[1]); maxY = Math.max(maxY, c[1]);
    }
  }
  if (!isFinite(minX)) return null;
  return { x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY) };
}

export function elementThumbnail(el) {
  const d = el.data || {};
  // Images preview as themselves (the hydrated URL is runtime-only; none, none).
  if (el.type === 'image') {
    return d.url ? `<img class="pl2-thumb" src="${esc(d.url)}" width="${SIZE}" height="${SIZE}" style="object-fit:cover" alt="" aria-hidden="true">` : '';
  }
  const b = box(el);
  if (!b) return '';
  const shape = (() => {
    switch (el.type) {
      case 'ellipse':
        return `<ellipse cx="${d.x}" cy="${d.y}" rx="${b.w / 2}" ry="${b.h / 2}" ${styleAttrs(d)}/>`;
      case 'rectangle': {
        const rot = d.rotation ? ` transform="rotate(${(d.rotation * 180) / Math.PI} ${d.x} ${d.y})"` : '';
        return `<rect x="${b.x}" y="${b.y}" width="${b.w}" height="${b.h}" ${styleAttrs(d)}${rot}/>`;
      }
      case 'polygon':
        return `<path d="${esc(coordsToPathData(d.coordinates, true))}" ${styleAttrs(d)}/>`;
      case 'line':
        return `<path d="${esc(coordsToPathData(d.coordinates, false))}" fill="none" stroke="${esc(d.strokeColor || '#555')}" vector-effect="non-scaling-stroke" stroke-width="1.5" stroke-linecap="round"/>`;
      default:
        return '';
    }
  })();
  if (!shape) return '';
  // Pad the viewBox slightly so strokes at the edge aren't clipped.
  const pad = Math.max(b.w, b.h) * 0.06;
  return `<svg class="pl2-thumb" width="${SIZE}" height="${SIZE}" viewBox="${b.x - pad} ${b.y - pad} ${b.w + pad * 2} ${b.h + pad * 2}" preserveAspectRatio="xMidYMid meet" aria-hidden="true">${shape}</svg>`;
}
